import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, LayoutGrid, List, ArrowUpRight } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "@/data/products";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { onImgError } from "@/lib/images";

export default function Products({ onOpenEnquiry }) {
  const [view, setView] = useState("grid");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const filtered = useMemo(() => {
    return PRODUCTS.filter(
      (p) =>
        (cat === "all" || p.category === cat) &&
        (q.trim() === "" ||
          p.name.toLowerCase().includes(q.toLowerCase()) ||
          p.short.toLowerCase().includes(q.toLowerCase())),
    );
  }, [q, cat]);

  return (
    <>
      <Helmet>
        <title>Products — Vortex Power Solutions</title>
        <meta
          name="description"
          content="Explore our complete range: Servo Stabilizers, Isolation Transformers, APSC, APC, Timers and Voltage Stabilizers."
        />
        <link rel="canonical" href="https://www.vortexworld.in/products" />
      </Helmet>

      {/* Hero — lighter tint */}
      <section className="bg-vortex-tint text-vortex-navy py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-light opacity-70 pointer-events-none" />
        <div className="container-wide relative">
          <div className="section-label mb-5">
            Product Catalog
          </div>
          <h1 className="font-heading font-extrabold text-5xl md:text-6xl tracking-tighter leading-[1.05] mb-4">
            Industrial power products.
          </h1>
          <p className="text-lg text-vortex-slate max-w-2xl">
            Explore our complete range of power conditioning equipment,
            manufactured in Chennai and shipped nationwide.
          </p>
        </div>
      </section>

      {/* Toolbar */}
      <section className="bg-white border-b border-vortex-border sticky top-20 z-30">
        <div className="container-wide py-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-vortex-slate" />
            <Input
              placeholder="Search products…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 rounded-full bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
              data-testid="products-search-input"
            />
          </div>

          <div className="flex-1 flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
            <button
              onClick={() => setCat("all")}
              className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                cat === "all"
                  ? "bg-vortex-navy text-white"
                  : "bg-vortex-alt text-vortex-slate hover:text-vortex-navy"
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCat(c.slug)}
                className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  cat === c.slug
                    ? "bg-vortex-navy text-white"
                    : "bg-vortex-alt text-vortex-slate hover:text-vortex-navy"
                }`}
                data-testid={`filter-${c.slug}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-1 bg-vortex-alt rounded-full p-1">
            <button
              onClick={() => setView("grid")}
              aria-label="Grid view"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                view === "grid"
                  ? "bg-white text-vortex-navy shadow-sm"
                  : "text-vortex-slate hover:text-vortex-navy"
              }`}
              data-testid="view-grid-btn"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              aria-label="List view"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                view === "list"
                  ? "bg-white text-vortex-navy shadow-sm"
                  : "text-vortex-slate hover:text-vortex-navy"
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Grid / List */}
      <section className="bg-vortex-alt py-16 min-h-[50vh]">
        <div className="container-wide">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-vortex-slate">
              No products match your search.
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.06, duration: 0.55 }}
                >
                  <Link
                    to={`/products/${p.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden border border-vortex-border shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all"
                    data-testid={`product-card-${p.slug}`}
                  >
                    <div className="aspect-[4/3] bg-vortex-alt overflow-hidden">
                      <img
                        src={p.image}
                        onError={onImgError}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading font-bold text-xl text-vortex-navy group-hover:text-vortex-blue transition-colors mb-2">
                        {p.name}
                      </h3>
                      <p className="text-sm text-vortex-slate leading-relaxed line-clamp-2 mb-4">
                        {p.short}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-vortex-border">
                        <span className="text-sm font-semibold text-vortex-navy">
                          View Details
                        </span>
                        <ArrowUpRight
                          className="w-4 h-4 text-vortex-navy group-hover:text-vortex-orange group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                          strokeWidth={2}
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.04, duration: 0.4 }}
                >
                  <Link
                    to={`/products/${p.slug}`}
                    className="group flex flex-col md:flex-row gap-6 bg-white rounded-2xl overflow-hidden border border-vortex-border shadow-sm hover:shadow-lg transition-all"
                    data-testid={`product-list-${p.slug}`}
                  >
                    <div className="md:w-72 flex-shrink-0 aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img
                        src={p.image}
                        onError={onImgError}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 md:py-8 md:pr-8 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-heading font-bold text-2xl text-vortex-navy group-hover:text-vortex-blue transition-colors mb-2">
                          {p.name}
                        </h3>
                        <p className="text-vortex-slate leading-relaxed mb-4">
                          {p.short}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {p.variants.slice(0, 4).map((v) => (
                            <span
                              key={v}
                              className="text-xs font-semibold text-vortex-navy bg-vortex-alt border border-vortex-border rounded-full px-3 py-1"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-vortex-orange font-semibold">
                        View Product
                        <ArrowUpRight
                          className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                          strokeWidth={2}
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16">
        <div className="container-wide flex flex-col md:flex-row items-center justify-between gap-6 bg-vortex-navy text-white rounded-3xl px-8 md:px-12 py-10 relative overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
          <div className="relative">
            <h3 className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight mb-2">
              Can&apos;t find what you need?
            </h3>
            <p className="text-white/75">
              We build custom ratings, enclosures and voltage windows.
            </p>
          </div>
          <Button
            onClick={onOpenEnquiry}
            className="relative rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 px-8 font-semibold"
            data-testid="products-custom-cta"
          >
            Talk to an Engineer
          </Button>
        </div>
      </section>
    </>
  );
}
